import { useCallback, useEffect, useRef } from "react";

/**
 * Task #2367 — Helpers for wiring an `AbortController` + "is the component
 * still mounted?" guard into dialog-bound mutations so the user can
 * deliberately cancel a long-running upload / AI extract / send.
 *
 * Task #2360 already gated dialog dismissal on `isPending` so a stray
 * backdrop click can't silently drop an in-flight result. This hook is
 * the next layer up: when the user explicitly chooses to abort, we tear
 * the underlying `fetch` down via `AbortController` (passed through
 * `apiRequest({ signal })` in `client/src/lib/queryClient.ts`), and the
 * mutation's `onSuccess` / `onError` callbacks bail out instead of
 * firing toasts or invalidating caches for a request the user no longer
 * cares about.
 */

export function isAbortError(err: unknown): boolean {
  if (!err) return false;
  if (err instanceof DOMException && err.name === "AbortError") return true;
  const anyErr = err as { name?: string };
  return anyErr?.name === "AbortError";
}

export function useIsMountedRef() {
  const ref = useRef(true);
  useEffect(() => {
    ref.current = true;
    return () => {
      ref.current = false;
    };
  }, []);
  return ref;
}

/**
 * Tracks the live `AbortController` for a dialog's primary in-flight
 * fetch. Call `begin()` from the mutation's `mutationFn` to mint a fresh
 * controller (and store its `signal` for `apiRequest`); call `cancel()`
 * from a "Cancel" button to tear the fetch down. The mutation's
 * `onSettled` should call `clear()` so we don't keep a stale reference
 * to a finished controller.
 */
export function useAbortRef() {
  const ref = useRef<AbortController | null>(null);

  const begin = useCallback(() => {
    ref.current?.abort();
    const controller = new AbortController();
    ref.current = controller;
    return controller;
  }, []);

  const cancel = useCallback(() => {
    const controller = ref.current;
    if (controller) {
      ref.current = null;
      controller.abort();
      return true;
    }
    return false;
  }, []);

  const clear = useCallback((controller?: AbortController) => {
    if (!controller || ref.current === controller) {
      ref.current = null;
    }
  }, []);

  return { begin, cancel, clear, ref };
}
